<template>
    <div class="fm-nav-root">
        <!-- ========================================== -->
        <!-- SESSION WARNING MODAL                      -->
        <!-- ========================================== -->
        <b-modal v-model="showSessionWarning"
                 title="Session expiring soon"
                 hide-footer
                 no-close-on-esc
                 no-close-on-backdrop
                 centered
                 content-class="fm-session-modal"
                 dialog-class="fm-session-dialog">
            <div class="fm-session">
                <div class="fm-session-clock" :class="{ 'is-urgent': secondsLeft <= 300 }">
                    <span class="fm-session-clock-icon"><span class="fm-ico"><b-icon icon="clock"></b-icon></span></span>
                    <div>
                        <div class="fm-session-clock-label">Time remaining</div>
                        <div class="fm-session-clock-time">{{ formatTime(secondsLeft) }}</div>
                    </div>
                </div>

                <p class="fm-session-text">
                    Your session ends in <strong>{{ minutesLeft }} minute{{ minutesLeft === 1 ? '' : 's' }}</strong>.
                    Renew it to keep working, or save your work now. Unsaved work may be lost when it ends.
                </p>

                <div class="fm-session-actions">
                    <button type="button" class="fm-btn fm-btn-ghost" @click="dismissWarning">
                        Remind me later
                    </button>
                    <button type="button" class="fm-btn fm-btn-primary" @click="extendSession" :disabled="isRefreshingToken">
                        <b-spinner small v-if="isRefreshingToken"></b-spinner>
                        <span v-else class="fm-ico"><b-icon icon="arrow-clockwise"></b-icon></span>
                        <span>{{ isRefreshingToken ? 'Renewing…' : 'Renew session' }}</span>
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- ========================================== -->
        <!-- MAIN NAVBAR                                -->
        <!-- ========================================== -->
        <b-navbar toggleable="md" type="dark" class="fm-navbar">
            <b-navbar-brand href="/" class="fm-brand">
                <span class="fm-brand-logo">FM</span>
                <span class="fm-brand-text">
                    <span class="fm-brand-name">Furniture Mecca</span>
                    <span class="fm-brand-sub">Inventory system</span>
                </span>
            </b-navbar-brand>

            <!-- Phone quick actions: always visible below 768px -->
            <div class="fm-quick d-md-none">
                <button v-if="showTimerInNav"
                        type="button"
                        class="fm-timer"
                        :class="{ 'is-urgent': secondsLeft <= 300 }"
                        @click="showSessionWarningManually"
                        aria-label="Session time remaining">
                    <span class="fm-ico"><b-icon icon="clock"></b-icon></span>
                    <span class="fm-timer-text">{{ formatTime(secondsLeft) }}</span>
                </button>
                <button v-if="authenticated" type="button" class="fm-icon-btn" @click="goToNotifications" aria-label="Notifications">
                    <span class="fm-ico"><b-icon icon="bell"></b-icon></span>
                    <span v-if="unreadCount > 0" class="fm-count">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
                </button>
                <button v-if="authenticated" type="button" class="fm-icon-btn" @click="refresh_packet" aria-label="Refresh system data">
                    <span class="fm-ico"><b-icon icon="cloud-download"></b-icon></span>
                </button>
                <b-navbar-toggle target="nav-collapse" class="fm-toggle">
                    <template #default="{ expanded }">
                        <span class="fm-ico"><b-icon :icon="expanded ? 'x' : 'list'"></b-icon></span>
                    </template>
                </b-navbar-toggle>
            </div>

            <b-collapse id="nav-collapse" v-model="navOpen" is-nav>
                <!-- Left -->
                <b-navbar-nav class="fm-nav-left">
                    <b-nav-item-dropdown v-if="authenticated" class="fm-visitor">
                        <template v-slot:button-content>
                            <span class="fm-nav-label">
                                <span class="fm-ico"><b-icon icon="people"></b-icon></span>
                                Visitor system
                            </span>
                        </template>
                        <b-dropdown-item to="/visitor/checkin/">
                            <span class="fm-ico"><b-icon icon="person-check"></b-icon></span>
                            Check-in
                        </b-dropdown-item>
                        <b-dropdown-item to="/visitor/dashboard/">
                            <span class="fm-ico"><b-icon icon="speedometer2"></b-icon></span>
                            Dashboard
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item to="/visitor/settings/">
                            <span class="fm-ico"><b-icon icon="gear"></b-icon></span>
                            Settings
                        </b-dropdown-item>
                    </b-nav-item-dropdown>
                </b-navbar-nav>

                <!-- Right (tablet + desktop) -->
                <b-navbar-nav class="ml-auto fm-nav-right d-none d-md-flex">
                    <li v-if="showTimerInNav" class="nav-item fm-nav-li">
                        <button type="button"
                                class="fm-timer"
                                :class="{ 'is-urgent': secondsLeft <= 300 }"
                                @click="showSessionWarningManually"
                                v-b-tooltip.hover
                                title="Session time remaining">
                            <span class="fm-ico"><b-icon icon="clock"></b-icon></span>
                            <span class="fm-timer-text">{{ formatTime(secondsLeft) }}</span>
                        </button>
                    </li>

                    <li v-if="authenticated" class="nav-item fm-nav-li">
                        <button type="button"
                                class="fm-icon-btn"
                                @click="goToNotifications"
                                v-b-tooltip.hover
                                title="Notifications"
                                aria-label="Notifications">
                            <span class="fm-ico"><b-icon icon="bell"></b-icon></span>
                            <span v-if="unreadCount > 0" class="fm-count">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
                        </button>
                    </li>

                    <b-nav-item-dropdown v-if="authenticated" right no-caret class="fm-user">
                        <template v-slot:button-content>
                            <span class="fm-avatar">
                                <img v-if="getUserImage()"
                                     :src="getUserImage()"
                                     :alt="displayName"
                                     @error="handleImageError" />
                                <span v-else class="fm-avatar-initials">{{ initials }}</span>
                            </span>
                            <span class="fm-user-text d-none d-lg-block">
                                <span class="fm-user-name">{{ displayName }}</span>
                                <!-- <span class="fm-user-role">
                                    {{ roleLabel }}<template v-if="locationText"> · {{ locationText }}</template>
                                </span> -->
                            </span>
                            <span class="fm-ico fm-chevron"><b-icon icon="chevron-down"></b-icon></span>
                        </template>

                        <b-dropdown-header class="fm-menu-head">
                            <span class="fm-menu-name">{{ displayName }}</span>
                            <span class="fm-menu-role">
                                {{ roleLabel }}<template v-if="locationText"> · {{ locationText }}</template>
                            </span>
                        </b-dropdown-header>
                        <b-dropdown-item @click="viewProfile">
                            <span class="fm-ico"><b-icon icon="person"></b-icon></span>
                            My profile
                        </b-dropdown-item>
                        <b-dropdown-item to="/notifications/">
                            <span class="fm-ico"><b-icon icon="bell"></b-icon></span>
                            Notifications
                            <span v-if="unreadCount > 0" class="fm-menu-count">{{ unreadCount }}</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="refresh_packet">
                            <span class="fm-ico"><b-icon icon="cloud-download"></b-icon></span>
                            Refresh system data
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="logout" class="fm-item-danger">
                            <span class="fm-ico"><b-icon icon="box-arrow-right"></b-icon></span>
                            Log out
                        </b-dropdown-item>
                    </b-nav-item-dropdown>
                </b-navbar-nav>

                <!-- Account panel (phones) -->
                <div v-if="authenticated" class="fm-mobile-user d-md-none">
                    <div class="fm-mobile-user-head">
                        <span class="fm-avatar is-lg">
                            <img v-if="getUserImage()"
                                 :src="getUserImage()"
                                 :alt="displayName"
                                 @error="handleImageError" />
                            <span v-else class="fm-avatar-initials">{{ initials }}</span>
                        </span>
                        <span class="fm-mobile-user-text">
                            <span class="fm-mobile-user-name">{{ displayName }}</span>
                            <span class="fm-mobile-user-role">
                                {{ roleLabel }}<template v-if="locationText"> · {{ locationText }}</template>
                            </span>
                        </span>
                    </div>
                    <button type="button" class="fm-mobile-link" @click="viewProfile">
                        <span class="fm-ico"><b-icon icon="person"></b-icon></span>
                        My profile
                    </button>
                    <router-link to="/notifications/" class="fm-mobile-link">
                        <span class="fm-ico"><b-icon icon="bell"></b-icon></span>
                        Notifications
                        <span v-if="unreadCount > 0" class="fm-menu-count">{{ unreadCount }}</span>
                    </router-link>
                    <button type="button" class="fm-mobile-link is-danger" @click="logout">
                        <span class="fm-ico"><b-icon icon="box-arrow-right"></b-icon></span>
                        Log out
                    </button>
                </div>
            </b-collapse>
        </b-navbar>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import axios from 'axios'

    export default {
        name: "Navigation",
        props: [],
        data: function(){
            return {
                authenticated: null,
                userDetails: {},
                packet: null,
                isMenuOpen: false,

                // Mobile menu + avatar
                navOpen: false,
                avatarFailed: false,

                // Session timer properties
                showSessionWarning: false,
                minutesLeft: 0,
                secondsLeft: 0,
                sessionCheckInterval: null,
                sessionTimerInterval: null,
                showTimerInNav: false,
                warningAlreadyShown: localStorage.getItem('sessionWarningDismissed') === 'true',
                isRefreshingToken: false,
                lastTokenCheckTime: 0,

                // Notification properties
                unreadCount: 0,
                notificationPollInterval: null,
                lastNotificationCheck: null
            }
        },
        computed: {
            displayName() {
                return (this.userDetails && (this.userDetails.fullname || this.userDetails.username)) || '';
            },
            initials() {
                return this.getInitials(this.displayName);
            },
            roleLabel() {
                return this.getUserRoleLabel(this.userDetails && this.userDetails.userrole);
            },
            locationText() {
                const loc = this.userLocation();
                return loc ? loc.text : '';
            }
        },
        watch: {
            // Close the phone menu after navigating
            '$route'() {
                this.navOpen = false;
            },
            // A new user/photo gets a fresh chance to load
            userDetails() {
                this.avatarFailed = false;
            }
        },
        methods: {
            // ==========================================
            // NOTIFICATION METHODS
            // ==========================================

            async fetchUnreadCount() {
                try {
                    if (!this.authenticated) return

                    const response = await axios.get('/api/notification/notifications/unread/count/', {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('userToken')}`
                        }
                    })

                    if (response.data && response.data.success) {
                        const newCount = response.data.unread_count || 0
                        if (this.unreadCount !== newCount) {
                            this.unreadCount = newCount
                            this.$root.$emit('unread-count-updated', newCount)
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch unread count:', error)
                }
            },

            startNotificationPolling() {
                if (this.notificationPollInterval) {
                    clearInterval(this.notificationPollInterval)
                }

                this.notificationPollInterval = setInterval(() => {
                    if (this.authenticated) {
                        this.fetchUnreadCount()
                    }
                }, 30000)

                document.addEventListener('visibilitychange', this.handleVisibilityChange)
            },

            stopNotificationPolling() {
                if (this.notificationPollInterval) {
                    clearInterval(this.notificationPollInterval)
                    this.notificationPollInterval = null
                }
                document.removeEventListener('visibilitychange', this.handleVisibilityChange)
            },

            handleVisibilityChange() {
                if (!document.hidden && this.authenticated) {
                    this.fetchUnreadCount()
                }
            },

            goToNotifications() {
                this.navOpen = false
                if (this.$route && this.$route.path === '/notifications/') return
                this.$router.push('/notifications/')
            },

            async markAllNotificationsRead() {
                try {
                    const response = await axios.post('/api/notification/notifications/read/all/', {}, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('userToken')}`
                        }
                    })

                    if (response.data && response.data.success) {
                        this.unreadCount = 0
                        this.$root.$emit('unread-count-updated', 0)
                    }
                } catch (error) {
                    console.error('Failed to mark all as read:', error)
                }
            },

            // ==========================================
            // SESSION TIMER METHODS
            // ==========================================

            startSessionTimer() {
                this.stopSessionTimer();

                const updateTimer = () => {
                    const token = localStorage.getItem('userToken');
                    if (!token || !this.authenticated) {
                        this.stopSessionTimer();
                        return;
                    }

                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        const currentTime = Math.floor(Date.now() / 1000);
                        this.secondsLeft = payload.exp - currentTime;
                        this.minutesLeft = Math.floor(this.secondsLeft / 60);

                        this.showTimerInNav = (this.secondsLeft > 0 && this.secondsLeft <= 900);

                        if (this.secondsLeft > 0 && this.secondsLeft <= 900) {
                            if (this.secondsLeft <= 900 && this.secondsLeft > 890) {
                                this.showSessionWarning = true;
                            }
                        } else {
                            this.showSessionWarning = false;

                            if (this.secondsLeft > 960) {
                                this.warningAlreadyShown = false;
                                localStorage.setItem('sessionWarningDismissed', 'false');
                            }
                        }

                        if (this.secondsLeft <= 0) {
                            this.handleSessionExpired();
                        }
                    } catch (error) {
                        console.error('Error updating timer:', error);
                        this.handleSessionExpired();
                    }
                };

                this.sessionTimerInterval = setInterval(updateTimer, 1000);
                this.sessionCheckInterval = setInterval(updateTimer, 30000);
                updateTimer();
            },

            stopSessionTimer() {
                if (this.sessionCheckInterval) {
                    clearInterval(this.sessionCheckInterval);
                    this.sessionCheckInterval = null;
                }
                if (this.sessionTimerInterval) {
                    clearInterval(this.sessionTimerInterval);
                    this.sessionTimerInterval = null;
                }
            },

            showSessionWarningManually() {
                const token = localStorage.getItem('userToken');
                if (!token || !this.authenticated) return;

                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const currentTime = Math.floor(Date.now() / 1000);
                    const secondsLeft = payload.exp - currentTime;

                    if (secondsLeft > 0) {
                        this.showSessionWarning = true;
                        this.secondsLeft = secondsLeft;
                        this.minutesLeft = Math.floor(secondsLeft / 60);
                    } else {
                        this.handleSessionExpired();
                    }
                } catch (error) {
                    console.error('Error checking token for manual warning:', error);
                }
            },

            handleSessionExpired() {
                this.stopSessionTimer();
                this.showSessionWarning = false;
                this.showTimerInNav = false;
                this.warningAlreadyShown = false;
                localStorage.removeItem('sessionWarningDismissed');

                this.$bvToast.toast(
                    'Your session has expired. Please log in again.',
                    {
                        title: 'Session Expired',
                        autoHideDelay: 5000,
                        variant: 'warning',
                        noCloseButton: false
                    }
                );

                setTimeout(() => {
                    this.logout();
                }, 2000);
            },

            dismissWarning() {
                this.showSessionWarning = false;

                this.$bvToast.toast(
                    `Session will expire in ${this.minutesLeft} minutes. Remember to save your work.`,
                    {
                        title: 'Session Reminder',
                        autoHideDelay: 3000,
                        variant: 'info',
                        noCloseButton: false
                    }
                );
            },

            extendSession() {
                this.refreshToken();
            },

            formatTime(seconds) {
                if (seconds <= 0) return 'Expired';

                const mins = Math.floor(seconds / 60);
                const secs = seconds % 60;

                if (mins > 0) {
                    return `${mins}:${secs.toString().padStart(2, '0')}`;
                }
                return `${secs}s`;
            },

            refreshToken() {
                this.isRefreshingToken = true;

                Requestor.postAsyncP('/api/user/refresh-token/', {}, this,
                    (response) => {
                        this.isRefreshingToken = false;

                        if (response.data && response.data.token) {
                            localStorage.setItem('userToken', response.data.token);
                            this.warningAlreadyShown = false;
                            localStorage.setItem('sessionWarningDismissed', 'false');
                            this.showSessionWarning = false;
                            this.showTimerInNav = false;

                            this.$bvToast.toast('Session renewed for another hour!', {
                                title: 'Session Renewed',
                                variant: 'success',
                                autoHideDelay: 4000,
                                toaster: 'b-toaster-top-center'
                            });

                            setTimeout(() => {
                                this.startSessionTimer();
                            }, 500);

                        } else if (response.data.error) {
                            this.handleRefreshError(response.data.error);
                        }
                    },
                    (error) => {
                        this.isRefreshingToken = false;
                        console.error('Token refresh error:', error);

                        let errorMessage = 'Could not renew session.';

                        if (error.response) {
                            if (error.response.status === 401) {
                                errorMessage = 'Your session has expired. Please log in again.';
                            } else if (error.response.data && error.response.data.error) {
                                errorMessage = error.response.data.error;
                            }
                        }

                        this.handleRefreshError(errorMessage);
                    }
                );
            },

            handleRefreshError(errorMessage) {
                this.$bvToast.toast(errorMessage, {
                    title: 'Session Renewal Failed',
                    variant: 'danger',
                    autoHideDelay: 5000
                });

                if (errorMessage.includes('expired') || errorMessage.includes('Please log in')) {
                    this.$bvModal.msgBoxConfirm(
                        'Your session could not be renewed. Would you like to save your work before logging out?',
                        {
                            title: 'Session Expired',
                            size: 'sm',
                            buttonSize: 'sm',
                            okVariant: 'primary',
                            okTitle: 'Save and Logout',
                            cancelTitle: 'Logout Now',
                            footerClass: 'p-2',
                            hideHeaderClose: false,
                            centered: true
                        }
                    ).then(shouldSave => {
                        if (shouldSave) {
                            this.$root.$emit('save-work-before-logout');

                            setTimeout(() => {
                                this.logout();
                            }, 2000);
                        } else {
                            this.logout();
                        }
                    });
                }
            },

            // ==========================================
            // USER IMAGE METHODS
            // ==========================================

            getUserImage() {
                if (this.avatarFailed) return null;
                if (!this.userDetails || !this.userDetails.properties || !this.userDetails.properties.image_url) {
                    return null;
                }

                var imageUrl = this.userDetails.properties.image_url;
                if (typeof imageUrl === 'string') {
                    imageUrl = imageUrl.trim();
                }

                if (!imageUrl || imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                    return null;
                }

                if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return 'https://corsproxy.io/?' + encodeURIComponent(imageUrl);
                }

                return imageUrl;
            },

            // Fall back to initials (reactive, so the styled placeholder renders)
            handleImageError() {
                console.warn('Image failed to load');
                this.avatarFailed = true;
            },

            getInitials(name) {
                if (!name) return '?';
                return name.split(' ')
                    .filter(Boolean)
                    .map(function(word) { return word[0]; })
                    .join('')
                    .toUpperCase()
                    .substring(0, 2);
            },

            getUserRoleLabel(role) {
                const roleMap = {
                    'admin': 'Admin',
                    'accountmanager': 'Account Manager',
                    'officeuser': 'Office User',
                    'officemanager': 'Office Manager',
                    'locationadmin': 'Location Admin',
                    'locationuser': 'Location User',
                    'locationstockperson': 'Location Stock Person',
                    'salesperson': 'Sales Person',
                    'warehouseuser': 'Warehouse User',
                    'warehousemanager': 'Warehouse Manager',
                    'accountsuser': 'Accounts User',
                    'housetruck': 'House Truck',
                    'deliverytruck': 'Delivery Truck'
                };
                return roleMap[role] || role || '';
            },

            viewProfile: function() {
                this.navOpen = false;
                if (this.userDetails && this.userDetails.username) {
                    this.$router.push({
                        name: 'UserProfile',
                        params: { username: this.userDetails.username }
                    });
                } else {
                    this.$bvToast.toast(
                        "Unable to navigate to profile",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                }
            },

            userLocation() {
                if (!this.packet || !this.packet.locations || !(this.userDetails && this.userDetails.properties && this.userDetails.properties.location_id)) return null;
                return this.packet.locations.find(
                    (loc) => loc.value === this.userDetails.properties.location_id
                ) || null;
            },

            refresh_packet: function(e){
                if (e && e.preventDefault){
                    e.preventDefault()
                }
                let path = "/api/user/get_tools_packet/";
                let data = {};
                let that = this;
                let s = function(response){
                    if (response.data.result){
                        let locations = (response.data.result.locations || []).map(function(d){
                            return {value: d.id, text: d.name, short: d.properties.short, address: d.address, email: d.email, phone: d.phone, allowed: d.allowed}
                        });
                        locations.splice(0, 0, {value: null, text: "All", short: "None", address: null, email: null, phone: null, allowed: []});

                        let locationsdict = locations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let sublocations = (response.data.result.sublocations || []).map(function(d){
                            return {value: d.id, text: d.name, location_id: d.location_id}
                        });
                        let sublocationsdict = sublocations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let locsubs = (response.data.result.locsubs || []);
                        let locsubsdict = locsubs.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let vendors = (response.data.result.vendors || []);
                        let vendorsdict = vendors.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let categories = (response.data.result.categories || []).map(function(d){
                            return {value: d.id, text: d.name}
                        });
                        let categoriesdict = categories.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let trucks = (response.data.result.trucks || []).map(function(d){
                            return {value: d.value, text: d.text}
                        });
                        let trucksdict = trucks.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let packet = {
                            locations: locations,
                            locationsdict: locationsdict,
                            sublocations: sublocations,
                            sublocationsdict: sublocationsdict,
                            locsubs: locsubs,
                            locsubsdict: locsubsdict,
                            vendors: vendors,
                            vendorsdict: vendorsdict,
                            categories: categories,
                            categoriesdict: categoriesdict,
                            trucks: trucks,
                            trucksdict: trucksdict
                        }

                        localStorage.setItem("packet", JSON.stringify(packet));
                        that.packet = packet;
                        that.$bvToast.toast(
                            "System data refreshed.",
                            {title: "Refreshed", autoHideDelay: 1500, variant: "success"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                }

                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading locations.",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }

                Requestor.getAsyncP(path, data, this, s, f);
            },

            CheckAuthenticated: function(){
                if(localStorage.userToken){
                    this.authenticated = true;
                    let userDetails = JSON.parse(localStorage.userDetails);
                    if (userDetails){
                        this.userDetails = userDetails;
                    }
                    this.startSessionTimer();
                    this.checkWarningOnPageLoad();

                    this.fetchUnreadCount();
                    this.startNotificationPolling();
                }
                else {
                    this.authenticated = null;
                    this.stopSessionTimer();
                    this.stopNotificationPolling();
                }
            },

            checkWarningOnPageLoad() {
                const token = localStorage.getItem('userToken');
                if (!token) return;

                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const currentTime = Math.floor(Date.now() / 1000);
                    const secondsLeft = payload.exp - currentTime;

                    if (secondsLeft > 0 && secondsLeft <= 900) {
                        this.showTimerInNav = true;
                        const warningDismissed = localStorage.getItem('sessionWarningDismissed') === 'true';
                        this.warningAlreadyShown = warningDismissed;
                    }
                } catch (error) {
                    console.error('Error checking token on page load:', error);
                }
            },

            logout: function(){
                this.navOpen = false;
                this.stopSessionTimer();
                this.stopNotificationPolling();
                localStorage.removeItem('sessionWarningDismissed');
                localStorage.clear();
                this.authenticated = null;
                this.unreadCount = 0;
                this.$router.push({ "path": "/" })
            },

            // ==========================================
            // NAVIGATION METHODS
            // ==========================================

            toggleSidebar() {
                this.isMenuOpen = !this.isMenuOpen
                this.$emit('toggle-sidebar', this.isMenuOpen)
            }
        },

        mounted: function(){
            this.CheckAuthenticated();
            let that = this;

            this.$root.$on("authenticated", (authenticated) => {
                this.authenticated = authenticated;
                if (authenticated) {
                    let userDetails = JSON.parse(localStorage.userDetails);
                    if (userDetails){
                        this.userDetails = userDetails;
                    }
                    this.startSessionTimer();
                    this.fetchUnreadCount();
                    this.startNotificationPolling();
                } else {
                    this.stopSessionTimer();
                    this.stopNotificationPolling();
                }
            });

            try {
                that.packet = JSON.parse(localStorage.getItem("packet"));
            } catch (e) {
                console.warn("No packet in localStorage");
            }
        },

        beforeDestroy() {
            this.stopSessionTimer();
            this.stopNotificationPolling();
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }
</script>

<style scoped>
/* ===================================================== */
/* TOKENS                                                */
/* ===================================================== */
.fm-nav-root {
    --fm-ink: #221F1C;
    --fm-ink-2: #2B2724;
    --fm-orange: #EE7A22;
    --fm-orange-dark: #D4620F;
    --fm-orange-soft: #FFF1E4;
    --fm-amber: #FFB06B;
    --fm-teal: #0F6E6B;
    --fm-danger: #D93D32;
    --fm-line: rgba(255, 255, 255, 0.12);
    --fm-muted: rgba(255, 255, 255, 0.6);
    font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

/* ===================================================== */
/* ICON BOX — centered by absolute positioning so global */
/* `display` overrides can't knock icons out of place    */
/* ===================================================== */
.fm-ico {
    position: relative !important;
    display: inline-block !important;
    width: 1em !important;
    height: 1em !important;
    flex-shrink: 0;
    vertical-align: -0.125em;
    line-height: 1;
}
.fm-ico >>> svg {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 1em !important;
    height: 1em !important;
    margin: 0 !important;
    transform: translate(-50%, -50%) !important;
    display: block !important;
}

/* ===================================================== */
/* NAVBAR                                                */
/* ===================================================== */
.fm-navbar {
    position: relative;
    z-index: 1030;
    min-height: 64px;
    background: var(--fm-ink) !important;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: max(16px, env(safe-area-inset-left, 0px));
    padding-right: max(16px, env(safe-area-inset-right, 0px));
    box-shadow: inset 0 -2px 0 var(--fm-orange);
}

/* Brand */
.fm-brand {
    display: flex !important;
    align-items: center;
    gap: 10px;
    margin-right: 20px;
    padding: 0;
    min-width: 0;
    text-decoration: none;
}
.fm-brand-logo {
    position: relative;
    display: inline-block !important;
    width: 40px;
    height: 40px;
    line-height: 40px !important;
    text-align: center;
    border-radius: 12px;
    background: var(--fm-orange);
    color: #fff;
    font-family: 'Sora', sans-serif;
    font-weight: 800;
    font-size: 15px;
    letter-spacing: -0.02em;
    flex-shrink: 0;
}
.fm-brand-text { display: inline-block !important; line-height: 1.15; min-width: 0; }
.fm-brand-name {
    display: block;
    font-family: 'Sora', sans-serif;
    font-weight: 800;
    font-size: 17px;
    color: #fff;
    letter-spacing: -0.01em;
    white-space: nowrap;
}
.fm-brand-sub {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--fm-muted);
    white-space: nowrap;
}

/* Shared icon button (bell, refresh) */
.fm-icon-btn {
    position: relative;
    display: inline-block;
    width: 42px;
    height: 42px;
    padding: 0;
    border-radius: 12px;
    border: 1px solid var(--fm-line);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.88);
    font-size: 18px;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.fm-icon-btn > .fm-ico {
    position: absolute !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.fm-icon-btn:hover { background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.24); color: #fff; }

.fm-count {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 99px;
    background: var(--fm-danger);
    color: #fff;
    border: 2px solid var(--fm-ink);
    font-size: 10.5px;
    font-weight: 800;
    line-height: 16px;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

/* Session timer pill */
.fm-timer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border-radius: 99px;
    border: 1px solid rgba(255, 176, 107, 0.5);
    background: rgba(255, 176, 107, 0.14);
    color: var(--fm-amber);
    font-size: 13px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    white-space: nowrap;
    animation: fmPulse 2s infinite;
}
.fm-timer.is-urgent {
    border-color: rgba(255, 120, 110, 0.6);
    background: rgba(217, 61, 50, 0.18);
    color: #FFB4AC;
}
@keyframes fmPulse {
    0%   { box-shadow: 0 0 0 0 rgba(255, 176, 107, 0.4); }
    70%  { box-shadow: 0 0 0 7px rgba(255, 176, 107, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 176, 107, 0); }
}

/* Focus rings */
.fm-icon-btn:focus-visible,
.fm-timer:focus-visible,
.fm-mobile-link:focus-visible,
.fm-brand:focus-visible {
    outline: 2px solid var(--fm-amber);
    outline-offset: 2px;
}
.fm-icon-btn:focus:not(:focus-visible),
.fm-timer:focus:not(:focus-visible) { outline: none; }

/* ===================================================== */
/* LEFT NAV (Visitor system)                             */
/* ===================================================== */
.fm-nav-label { display: inline-flex; align-items: center; gap: 8px; }
.fm-visitor >>> .dropdown-toggle {
    display: inline-flex !important;
    align-items: center;
    gap: 6px;
    padding: 9px 14px !important;
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.85) !important;
    font-weight: 700;
    font-size: 14.5px;
    background: transparent !important;
    border: 0 !important;
    transition: background 0.15s ease, color 0.15s ease;
}
.fm-visitor >>> .dropdown-toggle:hover,
.fm-visitor.show >>> .dropdown-toggle { background: rgba(255, 255, 255, 0.08) !important; color: #fff !important; }
.fm-visitor >>> .dropdown-toggle::after { margin-left: 2px; vertical-align: middle; }

/* ===================================================== */
/* RIGHT NAV (tablet + desktop)                          */
/* ===================================================== */
.fm-nav-right { align-items: center; gap: 10px; }
.fm-nav-li { display: flex; align-items: center; }

/* User toggle */
.fm-user >>> .dropdown-toggle {
    display: flex !important;
    align-items: center;
    gap: 10px;
    padding: 4px 12px 4px 4px !important;
    border-radius: 99px;
    border: 1px solid var(--fm-line) !important;
    background: rgba(255, 255, 255, 0.04) !important;
    transition: background 0.15s ease, border-color 0.15s ease;
}
.fm-user >>> .dropdown-toggle::after { display: none !important; }
.fm-user >>> .dropdown-toggle:hover,
.fm-user.show >>> .dropdown-toggle { background: rgba(255, 255, 255, 0.1) !important; border-color: rgba(255, 255, 255, 0.24) !important; }

.fm-avatar {
    position: relative;
    display: inline-block !important;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--fm-orange);
    color: #fff;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.16);
}
.fm-avatar img { display: block; width: 100%; height: 100%; object-fit: cover; }
.fm-avatar-initials {
    display: block !important;
    width: 100%;
    height: 100%;
    line-height: 36px;
    text-align: center;
    font-family: 'Sora', sans-serif;
    font-weight: 800;
    font-size: 13.5px;
}
.fm-avatar.is-lg { width: 46px; height: 46px; }
.fm-avatar.is-lg .fm-avatar-initials { line-height: 46px; font-size: 16px; }

.fm-user-text { text-align: left; line-height: 1.2; max-width: 220px; min-width: 0; }
.fm-user-name {
    display: block;
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.fm-user-role {
    display: block;
    color: var(--fm-muted);
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.fm-chevron { color: rgba(255, 255, 255, 0.6); font-size: 12px; transition: transform 0.2s ease; }
.fm-user.show .fm-chevron { transform: rotate(180deg); }

/* ===================================================== */
/* DROPDOWN MENUS (light)                                */
/* ===================================================== */
.fm-nav-root >>> .dropdown-menu {
    min-width: 230px;
    margin-top: 8px;
    padding: 6px;
    border: 1px solid #E5E1DA;
    border-radius: 14px;
    box-shadow: 0 24px 48px -18px rgba(34, 31, 28, 0.5);
    font-family: inherit;
}
.fm-nav-root >>> .dropdown-item {
    display: flex !important;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 9px;
    font-size: 14.5px;
    font-weight: 600;
    color: #221F1C;
}
.fm-nav-root >>> .dropdown-item .fm-ico { color: var(--fm-orange); font-size: 16px; }
.fm-nav-root >>> .dropdown-item:hover,
.fm-nav-root >>> .dropdown-item:focus,
.fm-nav-root >>> .dropdown-item.active,
.fm-nav-root >>> .dropdown-item.router-link-exact-active {
    background: var(--fm-orange-soft);
    color: #B5520B;
}
.fm-nav-root >>> .fm-item-danger .dropdown-item { color: #B42318; }
.fm-nav-root >>> .fm-item-danger .dropdown-item .fm-ico { color: #B42318; }
.fm-nav-root >>> .fm-item-danger .dropdown-item:hover { background: #FEF3F2; color: #912018; }
.fm-nav-root >>> .dropdown-divider { margin: 6px 4px; border-color: #ECE8E1; }

.fm-nav-root >>> .fm-menu-head .dropdown-header {
    padding: 8px 12px 10px;
    margin-bottom: 4px;
    border-bottom: 1px solid #ECE8E1;
    white-space: normal;
}
.fm-menu-name { display: block; font-size: 14.5px; font-weight: 800; color: #221F1C; }
.fm-menu-role { display: block; font-size: 12.5px; font-weight: 600; color: #736C63; margin-top: 2px; }
.fm-menu-count {
    margin-left: auto;
    min-width: 22px;
    padding: 1px 7px;
    border-radius: 99px;
    background: var(--fm-danger);
    color: #fff;
    font-size: 11.5px;
    font-weight: 800;
    text-align: center;
}

/* ===================================================== */
/* PHONE ACCOUNT PANEL                                   */
/* ===================================================== */
.fm-mobile-user {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid var(--fm-line);
}
.fm-mobile-user-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 6px 12px;
}
.fm-mobile-user-text { display: block; min-width: 0; line-height: 1.25; }
.fm-mobile-user-name {
    display: block;
    color: #fff;
    font-weight: 800;
    font-size: 15.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.fm-mobile-user-role {
    display: block;
    color: var(--fm-muted);
    font-size: 13px;
    font-weight: 600;
}
.fm-mobile-link {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 48px;
    padding: 0 12px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    font-size: 15px;
    font-weight: 700;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}
.fm-mobile-link .fm-ico { color: var(--fm-amber); font-size: 17px; }
.fm-mobile-link:hover,
.fm-mobile-link:active,
.fm-mobile-link.router-link-exact-active { background: rgba(255, 255, 255, 0.08); color: #fff; text-decoration: none; }
.fm-mobile-link.is-danger { color: #FFB4AC; }
.fm-mobile-link.is-danger .fm-ico { color: #FF8A80; }

/* ===================================================== */
/* PHONES (< 768px)                                      */
/* ===================================================== */
.fm-quick { display: flex; align-items: center; gap: 6px; margin-left: auto; }
.fm-quick >>> .navbar-toggler,
.fm-toggle {
    position: relative;
    width: 42px;
    height: 42px;
    padding: 0 !important;
    border-radius: 12px !important;
    border: 1px solid var(--fm-line) !important;
    background: rgba(255, 255, 255, 0.05);
    color: #fff !important;
    font-size: 22px;
}
.fm-quick >>> .navbar-toggler .fm-ico {
    position: absolute !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.fm-quick >>> .navbar-toggler:focus { outline: none; box-shadow: 0 0 0 2px rgba(255, 176, 107, 0.6); }

@media (max-width: 767.98px) {
    .fm-navbar { min-height: 60px; }
    .fm-brand { margin-right: 8px; }
    .fm-brand-logo { width: 36px; height: 36px; line-height: 36px !important; font-size: 14px; border-radius: 10px; }
    .fm-brand-sub { display: none; }

    /* Collapsed menu becomes a dark panel */
    .fm-navbar >>> .navbar-collapse {
        margin: 10px -4px 2px;
        padding: 8px;
        border-radius: 16px;
        background: var(--fm-ink-2);
        border: 1px solid var(--fm-line);
        max-height: calc(100vh - 90px);
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .fm-visitor { width: 100%; }
    .fm-visitor >>> .dropdown-toggle {
        width: 100%;
        min-height: 48px;
        padding: 0 12px !important;
        font-size: 15px;
        justify-content: space-between;
    }
    .fm-visitor >>> .dropdown-menu {
        position: static !important;
        float: none !important;
        transform: none !important;
        width: 100%;
        min-width: 0;
        margin: 2px 0 6px;
        padding: 4px 0 4px 14px;
        background: transparent;
        border: 0;
        box-shadow: none;
    }
    .fm-visitor >>> .dropdown-item {
        min-height: 46px;
        color: rgba(255, 255, 255, 0.85);
        font-size: 14.5px;
    }
    .fm-visitor >>> .dropdown-item .fm-ico { color: var(--fm-amber); }
    .fm-visitor >>> .dropdown-item:hover,
    .fm-visitor >>> .dropdown-item:focus,
    .fm-visitor >>> .dropdown-item.router-link-exact-active {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
    }
    .fm-visitor >>> .dropdown-divider { border-color: var(--fm-line); }
}

/* Small phones */
@media (max-width: 379.98px) {
    .fm-navbar { padding-left: max(10px, env(safe-area-inset-left, 0px)); padding-right: max(10px, env(safe-area-inset-right, 0px)); }
    .fm-brand-name { font-size: 15px; }
    .fm-quick { gap: 4px; }
    .fm-icon-btn,
    .fm-quick >>> .navbar-toggler { width: 38px; height: 38px; }
    .fm-timer { height: 32px; padding: 0 9px; font-size: 12px; }
}
/* Very narrow + timer showing: hide the brand name, keep the FM mark */
@media (max-width: 359.98px) {
    .fm-brand-text { display: none !important; }
}

/* ===================================================== */
/* TABLETS (768–991): avatar-only user chip              */
/* ===================================================== */
@media (min-width: 768px) and (max-width: 991.98px) {
    .fm-brand { margin-right: 12px; }
    .fm-brand-sub { display: none; }
    .fm-visitor >>> .dropdown-toggle { padding: 9px 10px !important; }
    .fm-user >>> .dropdown-toggle { padding: 4px 10px 4px 4px !important; }
}

/* ===================================================== */
/* TOUCH: bigger targets                                 */
/* ===================================================== */
@media (pointer: coarse) {
    .fm-icon-btn,
    .fm-quick >>> .navbar-toggler { width: 44px; height: 44px; }
    .fm-timer { height: 40px; }
    .fm-nav-root >>> .dropdown-item { min-height: 46px; }
    .fm-user >>> .dropdown-toggle { min-height: 46px; }
}

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
    .fm-timer { animation: none; }
    .fm-chevron, .fm-icon-btn, .fm-user >>> .dropdown-toggle { transition: none; }
}

/* ===================================================== */
/* SESSION MODAL BODY                                    */
/* ===================================================== */
.fm-session { color: #221F1C; }
.fm-session-clock {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #E5E1DA;
    border-left: 5px solid #C98A12;
    border-radius: 14px;
}
.fm-session-clock.is-urgent { border-left-color: #C93C30; }
.fm-session-clock-icon {
    position: relative;
    display: inline-block !important;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #FFF4DC;
    color: #C98A12;
    font-size: 20px;
    flex-shrink: 0;
}
.fm-session-clock.is-urgent .fm-session-clock-icon { background: #FCEBE8; color: #C93C30; }
.fm-session-clock-icon > .fm-ico {
    position: absolute !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.fm-session-clock-label { font-size: 12.5px; font-weight: 600; color: #736C63; }
.fm-session-clock-time {
    font-family: 'Sora', sans-serif;
    font-size: 26px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}
.fm-session-text { margin: 0 0 18px; font-size: 15px; line-height: 1.55; color: #4A4540; }
.fm-session-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }

.fm-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 44px;
    padding: 0 18px;
    border-radius: 12px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 14.5px;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.fm-btn-primary {
    background: #EE7A22;
    color: #fff;
    box-shadow: 0 8px 18px -8px rgba(238, 122, 34, 0.8);
}
.fm-btn-primary:hover:not(:disabled) { background: #D4620F; }
.fm-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }
.fm-btn-ghost { background: #fff; color: #221F1C; border-color: #E5E1DA; }
.fm-btn-ghost:hover { border-color: #EE7A22; color: #D4620F; }
.fm-btn:focus-visible { outline: 2px solid #EE7A22; outline-offset: 2px; }

@media (max-width: 479.98px) {
    .fm-session-actions { flex-direction: column-reverse; }
    .fm-session-actions .fm-btn { width: 100%; }
    .fm-session-clock-time { font-size: 23px; }
}
</style>

<style>
/* Modal chrome renders in <body>, so it needs unscoped rules */
.fm-session-modal.modal-content {
    border: 0;
    border-radius: 22px;
    overflow: hidden;
    font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.fm-session-modal .modal-header {
    background: #221F1C;
    color: #fff;
    border: 0;
    padding: 16px 22px;
    box-shadow: inset 0 -3px 0 #EE7A22;
}
.fm-session-modal .modal-title {
    font-family: 'Sora', 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 19px;
}
.fm-session-modal .modal-header .close {
    color: #fff;
    opacity: 0.8;
    text-shadow: none;
    font-size: 28px;
    min-width: 44px;
    min-height: 44px;
    margin: -12px -12px -12px auto;
}
.fm-session-modal .modal-body { padding: 22px; background: #F7F5F2; }
@media (max-width: 575.98px) {
    .fm-session-dialog.modal-dialog { margin: auto 12px; }
    .fm-session-modal .modal-body { padding: 18px 16px; }
}
</style>


// WEBPACK FOOTER //
// src/components/globals/Navigation.vue